/**
 * Converts the Clerk session into a Frappe one before the app renders.
 *
 * PLACEMENT IS LOAD-BEARING: this must wrap `<FrappeProvider>`, never sit inside it.
 * `FrappeProvider` opens the realtime socket at mount and Frappe's socket.io authenticates from
 * the `sid` cookie — mount it before the exchange resolves and the socket connects
 * unauthenticated, which silently kills `extract.done` and takes AI extraction with it. See
 * docs/bosun-go-live-checklist.md, "Realtime is dead without a Frappe session".
 *
 * `queryClient` arrives as a prop rather than through `useQueryClient()` for the same structural
 * reason: this component sits ABOVE `QueryClientProvider`, so the hook is not available here.
 *
 * Only mounted when Clerk is configured — see `resolveSessionBridgeEnabled` in `lib/auth.ts` for
 * the (separate) explicit `VITE_BOSUN_SESSION_BRIDGE=0` opt-out. The keyless dev path (no Clerk
 * key at all) never renders this component.
 *
 * KNOWN GAP, not fixed here (finding 7): a session recovered mid-flight by `withSessionRetry`
 * mints a fresh `sid` cookie, but the realtime socket.io connection `FrappeProvider` opened at
 * mount authenticated at handshake against the OLD `sid` and nothing here re-establishes it. It
 * may self-heal on reconnect, or may silently stop delivering `extract.done` — the exact failure
 * this gate's placement (above `FrappeProvider`, see the note below) exists to prevent at BOOT,
 * reappearing mid-session where this component can't reach it (the socket lives inside `children`,
 * opened by a provider this component only wraps). If AI extraction mysteriously stops working
 * after a long-idle session, this is the first thing to check.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useAuth, useClerk } from '@clerk/clerk-react'
import type { QueryClient } from '@tanstack/react-query'
import { ensureFrappeSession, setClerkTokenSource } from '../lib/api/frappeSession'
import { classifyGateError, describeGateError, type GateFailure } from '../lib/gateError'
import { useClerkIdentity } from './ClerkIdentityBridge'
import { signOutSequence } from '../lib/api/signOut'

type GateState =
  | { phase: 'loading' }
  | { phase: 'ready' }
  | { phase: 'error'; failure: GateFailure; detail: string | null }

export function FrappeSessionGate({
  queryClient,
  children,
}: {
  queryClient: QueryClient
  children: ReactNode
}) {
  const { getToken } = useAuth()
  const { signOut } = useClerk()
  const identity = useClerkIdentity()
  const [state, setState] = useState<GateState>({ phase: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    // Wrapped rather than passed directly: Clerk's `getToken` takes options we never use, and a
    // bare reference would widen the injected signature for no reason.
    setClerkTokenSource(() => getToken())

    let cancelled = false
    setState({ phase: 'loading' })

    ensureFrappeSession()
      .then((user) => {
        if (cancelled) return
        // Seed the cache `useCurrentUser` reads, so SessionProvider gets the real email and role
        // with no second round-trip. The key must match useMasters.ts's exactly.
        queryClient.setQueryData(['current-user'], user)
        setState({ phase: 'ready' })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setState({ phase: 'error', failure: classifyGateError(err), detail: describeGateError(err) })
      })

    return () => {
      cancelled = true
    }
    // `getToken` is included on principle, not because it needs to be: it is memoized on Clerk's
    // stable `isomorphicClerk` singleton, so its periodic token refresh does NOT produce a new
    // function reference and this effect does not re-fire from that. That matters — if it did,
    // every refresh would flip this gate back to 'loading' and unmount the entire app tree below
    // it, destroying exactly the in-progress `/compare/:id` state this design exists to protect.
    // Correct by Clerk's current dependency behaviour, not by construction; if a future Clerk
    // version stops memoizing `getToken`, this effect's re-run would reintroduce that failure.
  }, [getToken, queryClient, attempt])

  const handleSignOut = useCallback(() => {
    // `onCleared` mirrors `Layout.tsx`'s sign-out, which passes `() => queryClient.clear()` —
    // this component already holds `queryClient` as a prop (see the header), so there is no
    // reason for the two sign-out paths to disagree on cache clearing. Harmless today since a
    // sign-out from the gate means the app tree (and its cache) never rendered anything worth
    // clearing yet, but leaving it asymmetric invites a future bug the day that stops being true.
    void signOutSequence(() => signOut(), () => queryClient.clear())
  }, [signOut, queryClient])

  if (state.phase === 'ready') return <>{children}</>
  if (state.phase === 'loading') return <GateShell title="Signing you in…" />

  if (state.failure === 'unprovisioned') {
    return (
      <GateShell title="Your account isn't set up in Waves yet">
        <p>
          You're signed in as <strong>{identity?.email ?? 'this account'}</strong>, but there's no
          Waves user for that address. Ask an administrator to create one, then sign in again.
        </p>
        <button className="btn" onClick={handleSignOut}>
          Sign out
        </button>
      </GateShell>
    )
  }

  if (state.failure === 'unreachable') {
    return (
      <GateShell title="Can't reach the Waves server">
        <p>
          The sign-in request didn't get a response. If you're a developer, the usual cause is
          that the server hasn't allowed this origin yet (CORS).
        </p>
        {/* Without this, "Try again" is a dead end: `classifyGateError` buckets ANY status-less
            non-null error here — including `exchange()`'s own "no Clerk token source installed"
            Error, which retrying can never fix — and this gate blocks the whole tree, so
            `Layout.tsx`'s sign-out is unreachable. This is the only way out in that case. */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
          <button className="tbtn" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
        <GateDetail detail={state.detail} />
      </GateShell>
    )
  }

  return (
    <GateShell title="We couldn't complete sign-in">
      <p>Your session couldn't be verified. Try again, or sign out and back in.</p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </button>
        <button className="tbtn" onClick={handleSignOut}>
          Sign out
        </button>
      </div>
      <GateDetail detail={state.detail} />
    </GateShell>
  )
}

/** The server's own words, small and muted — see `describeGateError`. */
function GateDetail({ detail }: { detail: string | null }) {
  if (!detail) return null
  return <p style={{ margin: 0, fontSize: 12, color: 'var(--muted)' }}>{detail}</p>
}

/** Centred panel matching ClerkSignInScreen's framing, so the gate and the wall look like one flow. */
function GateShell({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
      }}
    >
      <div
        style={{
          background: 'var(--panel)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: '28px 32px',
          maxWidth: 460,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          color: 'var(--ink)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18 }}>{title}</h2>
        {children}
      </div>
    </div>
  )
}
